import React, { useState } from "react";
import * as Icons from "lucide-react";
import { Check } from "lucide-react";
import { Reveal, SectionHeader, CheckItem } from "../Shared";
import FleetMapIllustration from "../FleetMapIllustration";
import { DriverScoreVisual, FuelVisual, ColdChainVisual, MaintenanceVisual, AlertsFeedVisual } from "./TelematicsVisuals";
import { PlatformFlowVisual } from "./PlatformVisuals";

const VISUALS = {
  "fleet-map": FleetMapIllustration,
  "driver-score": DriverScoreVisual,
  fuel: FuelVisual,
  "cold-chain": ColdChainVisual,
  maintenance: MaintenanceVisual,
};

const Icon = ({ name, size = 20 }) => {
  const C = Icons[name] || Icons.Circle;
  return <C size={size} />;
};

export const ProblemSection = ({ data, visual: V = AlertsFeedVisual }) => (
  <section className="section bg-[var(--fr-bg-alt)]" data-testid="telematics-problem">
    <div className="container-x grid lg:grid-cols-[1fr_1.05fr] gap-12 items-center">
      <div>
        <SectionHeader eyebrow={data.eyebrow} title={data.title} subtitle={data.text} />
        <ul className="mt-10 space-y-5">
          {data.pains.map((p, i) => (
            <Reveal key={p.title} as="li" delay={i * 60} className="flex items-start gap-4" data-testid={`telematics-pain-${i}`}>
              <span className="w-10 h-10 rounded-xl bg-white border border-[var(--fr-border)] text-[var(--fr-primary-dark)] flex items-center justify-center flex-shrink-0">
                <Icon name={p.icon} size={18} />
              </span>
              <div>
                <div className="font-display font-bold">{p.title}</div>
                <p className="text-sm text-[var(--fr-ink-2)] mt-1 leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </ul>
      </div>
      <Reveal delay={120} className="card p-3 shadow-[0_30px_60px_-30px_rgba(14,14,16,0.25)]">
        <div className="img-tile aspect-[6/5] bg-white">
          <V className="w-full h-full" />
        </div>
      </Reveal>
    </div>
  </section>
);

export const CapabilitiesSection = ({ data, visuals = VISUALS }) => {
  const [active, setActive] = useState(data.items[0].id);
  const item = data.items.find((c) => c.id === active);
  const Visual = visuals[item.visual];
  return (
    <section className="section" id="capabilities" data-testid="telematics-capabilities">
      <div className="container-x">
        <SectionHeader eyebrow={data.eyebrow} title={data.title} subtitle={data.subtitle} />
        <div className="mt-10 flex gap-2 overflow-x-auto md:flex-wrap lg:flex-nowrap lg:overflow-visible pb-2 -mx-1 px-1" role="tablist" data-testid="telematics-tabs">
          {data.items.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={active === c.id}
              onClick={() => setActive(c.id)}
              className={`inline-flex items-center gap-2 h-11 px-4 lg:px-3.5 rounded-full text-sm lg:text-[13px] font-semibold whitespace-nowrap border transition-colors flex-shrink-0 ${
                active === c.id ? "bg-[var(--fr-ink)] text-white border-[var(--fr-ink)]" : "bg-white text-[var(--fr-ink-2)] border-[var(--fr-border)] hover:border-[var(--fr-ink)]"
              }`}
              data-testid={`telematics-tab-${c.id}`}
            >
              <Icon name={c.icon} size={16} /> {c.label}
            </button>
          ))}
        </div>
        <div key={item.id} className="mt-8 grid lg:grid-cols-2 gap-10 items-center" data-testid={`telematics-panel-${item.id}`}>
          <div className="fade-up">
            <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{item.title}</h3>
            <p className="mt-4 text-lg text-[var(--fr-ink-2)] leading-relaxed">{item.text}</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {item.bullets.map((b) => (
                <CheckItem key={b}>{b}</CheckItem>
              ))}
            </ul>
          </div>
          <div className="card p-3 fade-up delay-1">
            <div className="img-tile aspect-[6/5] bg-white">
              <Visual className="w-full h-full" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const PlatformSection = ({ data }) => (
  <section className="section bg-[var(--fr-bg-alt)]" id="platform" data-testid="telematics-platform">
    <div className="container-x">
      <SectionHeader eyebrow={data.eyebrow} title={data.title} subtitle={data.subtitle} className="max-w-4xl" />
      <div className="mt-12 grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-center">
        <ol className="relative border-l border-[var(--fr-border)] ml-3 space-y-8">
          {data.steps.map((s, i) => (
            <Reveal key={s.n} as="li" delay={i * 90} className="pl-8 relative" data-testid={`telematics-platform-step-${i}`}>
              <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-[var(--fr-ink)] text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
              <h3 className="font-display font-bold text-lg leading-tight">{s.title}</h3>
              <p className="mt-2 text-sm text-[var(--fr-ink-2)] leading-relaxed">{s.text}</p>
            </Reveal>
          ))}
        </ol>
        <Reveal delay={150}>
          <div className="card p-3 shadow-[0_30px_60px_-30px_rgba(14,14,16,0.25)]">
            <div className="img-tile aspect-[6/5] bg-white">
              <PlatformFlowVisual className="w-full h-full" />
            </div>
          </div>
          <p className="mt-4 text-sm text-[var(--fr-muted)] flex items-start gap-2">
            <Check size={15} className="mt-[3px] text-[var(--fr-primary)] flex-shrink-0" strokeWidth={2.5} /> {data.note}
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

export const AlertsSection = ({ data }) => (
  <section className="section" data-testid="telematics-alerts">
    <div className="container-x grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
      <SectionHeader eyebrow={data.eyebrow} title={data.title} subtitle={data.subtitle} />
      <Reveal delay={100}>
        <div className="flex flex-wrap gap-2" data-testid="telematics-alert-list">
          {data.list.map((a, i) => (
            <span key={a} className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-[var(--fr-border)] text-sm font-medium reveal is-visible" style={{ transitionDelay: `${i * 40}ms` }}>
              <span className="w-2 h-2 rounded-full bg-[var(--fr-primary)]" /> {a}
            </span>
          ))}
        </div>
        <div className="mt-8 card p-5 flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="eyebrow">Delivered via</span>
          {data.channels.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 text-sm font-semibold">
              <Check size={14} className="text-[var(--fr-primary)]" strokeWidth={2.5} /> {c}
            </span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export const ReportsSection = ({ data }) => (
  <section className="section pt-0" data-testid="telematics-reports">
    <div className="container-x">
      <Reveal className="dark-block px-6 py-14 md:px-14 md:py-20">
        <span className="eyebrow text-white/60">{data.eyebrow}</span>
        <h2 className="h-section text-white mt-4 max-w-2xl">{data.title}</h2>
        <p className="mt-5 text-lg text-white/70 max-w-2xl leading-relaxed">{data.subtitle}</p>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.kpis.map((k, i) => (
            <Reveal key={k.value} delay={i * 60} className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid={`telematics-kpi-${i}`}>
              <div className="font-display font-bold text-2xl md:text-3xl tracking-tight text-white">{k.value}</div>
              <div className="mt-2 text-sm text-white/60 leading-relaxed">{k.label}</div>
            </Reveal>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export const IndustriesSection = ({ data }) => (
  <section className="section bg-[var(--fr-bg-alt)]" data-testid="telematics-industries">
    <div className="container-x">
      <SectionHeader eyebrow={data.eyebrow} title={data.title} />
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.items.map((it, i) => (
          <Reveal key={it.title} delay={(i % 3) * 70} className="card card-hover p-6 flex items-start gap-4" data-testid={`telematics-industry-${i}`}>
            <span className="w-10 h-10 rounded-xl bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center flex-shrink-0">
              <Icon name={it.icon} size={18} />
            </span>
            <div>
              <h3 className="font-display font-bold">{it.title}</h3>
              <p className="mt-1.5 text-sm text-[var(--fr-ink-2)] leading-relaxed">{it.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ResultsSection = ({ data }) => (
  <section className="section" data-testid="telematics-results">
    <div className="container-x">
      <SectionHeader eyebrow={data.eyebrow} title={data.title} subtitle={data.subtitle} />
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.items.map((r, i) => (
          <Reveal key={`${r.value}-${r.label}`} delay={i * 70} className="card p-6 border-l-4 !border-l-[var(--fr-primary)]" data-testid={`telematics-result-${i}`}>
            <div className="font-display font-bold text-4xl tracking-tight text-[var(--fr-primary-dark)]">{r.value}</div>
            <div className="mt-2 font-semibold">{r.label}</div>
            <div className="mt-1.5 text-sm text-[var(--fr-muted)] leading-snug">{r.who}</div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
